import { api } from '../api/client';
import { uploadErrorMessageKey } from '../api/uploadErrors';
import { encryptGuestReply, decodeReplyPublicKey } from '../crypto/infoShareReply';
import { applyI18n, t } from '../i18n';
import { bindLocaleRefresh } from '../i18n/bindLocaleRefresh';
import { setCryptoBusy, setCryptoBusyProgress } from './cryptoBusy';
import { bindMessageInputSizing } from './messageInput';
import { mountInfoShareAttachmentField } from './infoShareAttachmentField';

/** Design §10.3: single-use + reply still allows multiple guest replies until expiry/revoke. */
export function guestReplyAllowsAnother(allowGuestReply: boolean): boolean {
  return allowGuestReply;
}

export function mountGuestReplyForm(
  container: HTMLElement,
  token: string,
  replyAllowsFiles: boolean,
  remainingReplyFiles: number | null,
  remainingReplyBytes: number | null,
  allowAnotherReply: boolean,
): void {
  const section = document.createElement('article');
  section.className = 'module-section guest-reply';
  section.innerHTML = `
    <div id="guest-reply-form-area">
      <h2 data-i18n="info.guest.replyTitle"></h2>
      <p class="muted" id="guest-reply-budget"></p>
      <form class="form-grid guest-reply-form" id="guest-reply-form">
        <label class="message-field">
          <span data-i18n="info.guest.replyMessage"></span>
          <textarea class="message-input" name="text" required></textarea>
        </label>
        <div id="guest-reply-files-host" hidden></div>
        <button type="submit" class="button primary">
          <span data-i18n="info.guest.replySubmit"></span>
        </button>
      </form>
    </div>
    <div id="guest-reply-sent" class="guest-reply-sent" hidden>
      <p class="guest-reply-sent__message" data-i18n="info.guest.replySent"></p>
      <p class="muted" data-i18n="info.guest.replySentDetail"></p>
      ${allowAnotherReply ? `<button type="button" class="button primary" id="guest-reply-another">
        <span data-i18n="info.guest.replyAnother"></span>
      </button>` : ''}
    </div>
    <p class="error-text" id="guest-reply-error" hidden></p>
  `;

  container.append(section);
  applyI18n(section);

  const formArea = section.querySelector('#guest-reply-form-area') as HTMLDivElement;
  const budget = section.querySelector('#guest-reply-budget') as HTMLParagraphElement;
  const form = section.querySelector('#guest-reply-form') as HTMLFormElement;
  const sentPanel = section.querySelector('#guest-reply-sent') as HTMLDivElement;
  const error = section.querySelector('#guest-reply-error') as HTMLParagraphElement;
  const replyMessageInput = form.querySelector('textarea[name="text"]') as HTMLTextAreaElement;
  const filesHost = section.querySelector('#guest-reply-files-host') as HTMLDivElement;

  let attachmentField: ReturnType<typeof mountInfoShareAttachmentField> | null = null;
  if (replyAllowsFiles) {
    filesHost.hidden = false;
    attachmentField = mountInfoShareAttachmentField(filesHost, {
      labelKey: 'info.guest.replyAttachments',
      hintKey: 'info.guest.replyAttachmentsHint',
    });
  }

  bindMessageInputSizing(replyMessageInput);

  let budgetFiles: number | null = remainingReplyFiles;
  let budgetBytes: number | null = remainingReplyBytes;

  function updateBudgetDisplay(files: number | null, bytes: number | null): void {
    budgetFiles = files;
    budgetBytes = bytes;
    if (replyAllowsFiles && (files !== null || bytes !== null)) {
      const parts: string[] = [];
      if (files !== null) {
        parts.push(`${files} ${t('info.guest.replyBudgetFilesLabel')}`);
      }
      if (bytes !== null) {
        const mb = Math.max(0, Math.round(bytes / (1024 * 1024)));
        parts.push(`${mb} ${t('info.guest.replyBudgetSizeLabel')}`);
      }
      budget.textContent = parts.join(' · ');
      budget.hidden = false;
    } else {
      budget.hidden = true;
    }
  }

  updateBudgetDisplay(remainingReplyFiles, remainingReplyBytes);

  const unbindLocale = bindLocaleRefresh(() => {
    updateBudgetDisplay(budgetFiles, budgetBytes);
    if (!error.hidden && error.dataset.errorKey) {
      error.textContent = t(error.dataset.errorKey);
    }
  });

  const localeObserver = new MutationObserver(() => {
    if (!section.isConnected) {
      unbindLocale();
      attachmentField?.dispose();
      localeObserver.disconnect();
    }
  });
  localeObserver.observe(document.body, { childList: true, subtree: true });

  function showFormArea(): void {
    formArea.hidden = false;
    sentPanel.hidden = true;
    error.hidden = true;
    form.reset();
    attachmentField?.clear();
    bindMessageInputSizing(replyMessageInput);
  }

  function showSentConfirmation(): void {
    formArea.hidden = true;
    sentPanel.hidden = false;
    error.hidden = true;
  }

  section.querySelector('#guest-reply-another')?.addEventListener('click', () => {
    void (async () => {
      try {
        const replyKey = await api.getInfoShareReplyKey(token);
        updateBudgetDisplay(replyKey.remainingReplyFiles, replyKey.remainingReplyBytes);
      } catch {
        // Budget display is optional; form still works for text-only replies.
      }
      showFormArea();
    })();
  });

  form.addEventListener('submit', (event) => {
    event.preventDefault();
    void (async () => {
      error.hidden = true;
      const formData = new FormData(form);
      const text = String(formData.get('text') ?? '').trim();
      if (!text) {
        return;
      }

      const selectedFiles = replyAllowsFiles
        ? (attachmentField?.getFiles() ?? [])
        : [];

      if (replyAllowsFiles && selectedFiles.length > 0) {
        if (budgetFiles !== null && selectedFiles.length > budgetFiles) {
          error.hidden = false;
          error.dataset.errorKey = 'info.guest.replyBudgetExceeded';
          error.textContent = t('info.guest.replyBudgetExceeded');
          return;
        }

        if (budgetBytes !== null) {
          const totalBytes = selectedFiles.reduce((sum, file) => sum + file.size, 0);
          if (totalBytes > budgetBytes) {
            error.hidden = false;
            error.dataset.errorKey = 'info.guest.replyBudgetExceeded';
            error.textContent = t('info.guest.replyBudgetExceeded');
            return;
          }
        }
      }

      setCryptoBusy(section, form, true, {
        titleKey: 'info.guest.replySending',
        detailKey: 'info.guest.replySendingDetail',
        icon: 'hourglass',
      });

      try {
        const replyKey = await api.getInfoShareReplyKey(token);
        const publicKey = decodeReplyPublicKey(replyKey.replyEncryptionPublicKey);
        const encrypted = await encryptGuestReply({
          text,
          files: selectedFiles.length > 0 ? selectedFiles : undefined,
          infoShareId: replyKey.infoShareId,
          ownerUserId: replyKey.ownerUserId,
          replyPublicKey: publicKey,
          onProgress: (progress) => {
            setCryptoBusyProgress(section, progress);
          },
        });

        await api.submitInfoShareReply(token, {
          wrappedContentKey: encrypted.wrappedContentKey,
          manifestBlobId: encrypted.manifestBlobId,
          attachmentCount: encrypted.attachmentCount,
          attachmentBytes: encrypted.attachmentBytes,
          attachmentBlobIds: encrypted.attachmentBlobIds,
        });

        showSentConfirmation();
      } catch (cause) {
        console.error('Guest reply failed', cause);
        error.hidden = false;
        const errorKey = uploadErrorMessageKey(cause, 'info.guest.replyFailed');
        error.dataset.errorKey = errorKey;
        error.textContent = t(errorKey);
      } finally {
        setCryptoBusy(section, form, false, {
          titleKey: 'info.guest.replySending',
          detailKey: 'info.guest.replySendingDetail',
        });
      }
    })();
  });
}
